import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from 'antd/lib/input';
import { useAppContext } from 'src/app/state';
import { IoMdClose } from 'react-icons/io';
import { AppRoutes } from 'src/types/app-routes.enum';
import Button from 'src/app/components/kit/button/Button';
import Text from 'src/app/components/kit/text/Text';
import Dropdown from 'src/app/components/kit/dropdown/Dropdown';
import DateTimePicker from 'src/app/components/kit/date-time-picker/DateTimePicker';
import { getPreviousDate } from 'src/app/utils/calendar.utils';
import { ActionType } from 'src/app/state/action-type.enum';
import { DropdownOption } from 'src/types/dropdown.types';
import styles from './DoctorAppointmentPage.module.css';

const { TextArea } = Input;

const DoctorAppointmentPage = () => {
  const navigate = useNavigate();
  const {
    state: { doctorAppointment },
    dispatch,
  } = useAppContext();
  const [appointment, setAppointment] = useState(
    doctorAppointment ?? {
      appointmentDate: undefined,
      doctorType: undefined,
      notes: undefined,
    }
  );
  const handleWindowClose = () => navigate(AppRoutes.Home);
  const disabledBefore = useMemo(() => getPreviousDate(new Date()), []);
  const doctorTypes: DropdownOption[] = [
    { key: '1', value: 'Neurologist' },
    { key: '2', value: 'General practitioner' },
    { key: '3', value: 'Other' },
  ];
  const onSelectAppointmentDate = (answer: Date) =>
    setAppointment((prev) => ({
      ...prev,
      appointmentDate: answer,
    }));
  const onSelectDoctor = (answer: DropdownOption) =>
    setAppointment((prev) => ({
      ...prev,
      doctorType: answer.value,
    }));
  const onChangeNotes = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    setAppointment((prev) => ({
      ...prev,
      notes: e.target.value,
    }));
  const onSaveAppointment = () => {
    dispatch({
      type: ActionType.SET_DOCTOR_APPOINTMENT,
      payload: { doctorAppointment: appointment },
    });
    handleWindowClose();
  };
  const onDeleteAppointment = () => {
    dispatch({
      type: ActionType.SET_DOCTOR_APPOINTMENT,
      payload: { doctorAppointment: undefined },
    });
    handleWindowClose();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Button
          type='transparent'
          width='content-width'
          style={{ padding: 0, height: '21.6px' }}
          onClick={handleWindowClose}
        >
          <IoMdClose size={20} />
        </Button>
      </div>
      <div className={styles.content}>
        <Text tag='h2' size='m-large' weight='bold'>
          {doctorAppointment ? 'Edit' : 'Add'} visit
        </Text>
        <Text weight='bold'>Upcoming appointment</Text>
        <DateTimePicker
          showTimePicker
          minDate={disabledBefore}
          selectedDate={appointment.appointmentDate}
          handleSelectDateTime={onSelectAppointmentDate}
        />
        <Text weight='bold'>Doctor type</Text>
        <Dropdown
          placeholder='Select doctor type'
          options={doctorTypes}
          value={
            doctorTypes.find((type) => type.value === appointment.doctorType) ??
            null
          }
          onChange={onSelectDoctor}
        />
        <Text weight='bold'>Notes (optional)</Text>
        <TextArea
          rows={4}
          placeholder='Enter notes'
          value={appointment.notes}
          onChange={onChangeNotes}
          style={{ fontSize: '16px' }}
        />
        <Text weight='bold'>You will receive 2 reminders</Text>
        <ul>
          <li>1 day before appointment</li>
          <li>2 hrs before appointment</li>
        </ul>
      </div>
      <div className={styles.footer}>
        {doctorAppointment ? (
          <>
            <div className={styles.flex1}>
              <Button
                type='delete'
                style={{ width: '100%', marginLeft: 0 }}
                width='full-width'
                onClick={onDeleteAppointment}
              >
                Delete
              </Button>
            </div>
            <div className={styles.flex1}>
              <Button
                type='primary'
                style={{ width: '100%', marginLeft: 0 }}
                width='full-width'
                onClick={onSaveAppointment}
                isReadOnly={
                  !appointment.appointmentDate || !appointment.doctorType
                }
              >
                Save
              </Button>
            </div>
          </>
        ) : (
          <Button
            type='primary'
            width='full-width'
            style={{ width: '100%', marginLeft: 0 }}
            onClick={onSaveAppointment}
            isReadOnly={!appointment.appointmentDate || !appointment.doctorType}
          >
            Add appointment
          </Button>
        )}
      </div>
    </div>
  );
};

export default DoctorAppointmentPage;
